import useSearchParams from "hooks/useSearchParams";
import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { deleteEmptyObjects } from "utils";

const useBooksFilters = () => {
	const [firstPage, setFirstPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(40);
	const [page, setPage] = useState(1);
	const [categoryIs, setCategoryIs] = useState("");
	const [genreIs, setGenreIs] = useState("");
	const [publisherIs, setPublisherIs] = useState("");
	const [pricingModelIs, setPricingModelIs] = useState("");
	const [searchTerm, setSearchTerm] = useState("");
	const [openFilters, setOpenFilters] = useState("");
	const [focusSearchInput, setFocusSearchInput] = useState("");
	const [countryIs, setCountryIs] = useState("");
	const [publicationDateFrom, setPublicationDateFrom] = useState("");
	const [publicationDateTo, setPublicationDateTo] = useState("");
	const [priceMin, setPriceMin] = useState("");
	const [priceMax, setPriceMax] = useState("");
	const [format, setFormat] = useState("");
	const [availability, setAvailability] = useState("");
	const [pageCountMin, setPageCountMin] = useState("");
	const [pageCountMax, setPageCountMax] = useState("");
	const [language, setLanguage] = useState("");

	const { getParam, setParam, deleteParam, hasParam } = useSearchParams();

	const { push } = useHistory();
	const { pathname } = useLocation();

	useEffect(() => {
		if (hasParam("open_filters")) {
			const openFiltersParam =
				getParam("open_filters")?.length > 0 ? getParam("open_filters") : "";
			setOpenFilters(openFiltersParam);
		}

		if (hasParam("focus_search_input")) {
			const focusSearchInputParam =
				getParam("focus_search_input")?.length > 0
					? getParam("focus_search_input")
					: "";
			setFocusSearchInput(focusSearchInputParam);
		}

		if (hasParam("page")) {
			const pageParam = getParam("page")?.length > 0 ? getParam("page") : 1;
			setPage(pageParam);
		}
		if (hasParam("rows")) {
			const rowsPerPageParam =
				getParam("rows")?.length > 0 ? getParam("rows") : 40;
			setRowsPerPage(rowsPerPageParam);
		}

		if (hasParam("first_page")) {
			const firstPageParam =
				getParam("first_page")?.length > 0 ? getParam("first_page") : 0;
			setFirstPage(firstPageParam);
		}

		if (hasParam("category_is")) {
			const categoryIsParam =
				getParam("category_is")?.length > 0 ? getParam("category_is") : "";
			setCategoryIs(categoryIsParam);
		}

		if (hasParam("country_is")) {
			const countryIsParam =
				getParam("country_is")?.length > 0 ? getParam("country_is") : "";
			setCountryIs(countryIsParam);
		}

		if (hasParam("genre_is")) {
			const genreIsParam =
				getParam("genre_is")?.length > 0 ? getParam("genre_is") : "";
			setGenreIs(genreIsParam);
		}

		if (hasParam("publisher_is")) {
			const publisherIsParam =
				getParam("publisher_is")?.length > 0 ? getParam("publisher_is") : "";
			setPublisherIs(publisherIsParam);
		}

		if (hasParam("pricing_model_is")) {
			const pricingModelIsParam =
				getParam("pricing_model_is")?.length > 0
					? getParam("pricing_model_is")
					: "";
			setPricingModelIs(pricingModelIsParam);
		}

		if (hasParam("search_term")) {
			const searchTermParam =
				getParam("search_term")?.length > 0 ? getParam("search_term") : "";
			setSearchTerm(searchTermParam);
		}

		if (hasParam("publication_date_from")) {
			setPublicationDateFrom(getParam("publication_date_from") || "");
		}
		if (hasParam("publication_date_to")) {
			setPublicationDateTo(getParam("publication_date_to") || "");
		}
		if (hasParam("price_min")) {
			setPriceMin(getParam("price_min") || "");
		}
		if (hasParam("price_max")) {
			setPriceMax(getParam("price_max") || "");
		}
		if (hasParam("format")) {
			setFormat(getParam("format") || "");
		}
		if (hasParam("availability")) {
			setAvailability(getParam("availability") || "");
		}
		if (hasParam("page_count_min")) {
			setPageCountMin(getParam("page_count_min") || "");
		}
		if (hasParam("page_count_max")) {
			setPageCountMax(getParam("page_count_max") || "");
		}
		if (hasParam("language")) {
			setLanguage(getParam("language") || "");
		}

		return () => {
			deleteParam("open_filters");
			deleteParam("page");
			deleteParam("rows");
			deleteParam("first_page");
			deleteParam("category_is");
			deleteParam("genre_is");
			deleteParam("publisher_is");
			deleteParam("pricing_model_is");
			deleteParam("search_term");
			deleteParam("country_is");
			deleteParam("focus_search_input");
			deleteParam("publication_date_from");
			deleteParam("publication_date_to");
			deleteParam("price_min");
			deleteParam("price_max");
			deleteParam("format");
			deleteParam("availability");
			deleteParam("page_count_min");
			deleteParam("page_count_max");
			deleteParam("language");
		};
	}, [hasParam, getParam, deleteParam]);

	const changeAllFiltersAtOnce = (data) => {
		const values = deleteEmptyObjects(data);

		setFirstPage(values?.first_page || "");
		setRowsPerPage(values?.rows || "");
		setPage(values?.page || "");
		setCategoryIs(values?.category_is || "");
		setGenreIs(values?.genre_is || "");
		setPublisherIs(values?.publisher_is || "");
		setPricingModelIs(values?.pricing_model_is || "");
		setSearchTerm(values?.search_term || "");
		setCountryIs(values?.country_is || "");
		setPublicationDateFrom(values?.publication_date_from || "");
		setPublicationDateTo(values?.publication_date_to || "");
		setPriceMin(values?.price_min || "");
		setPriceMax(values?.price_max || "");
		setFormat(values?.format || "");
		setAvailability(values?.availability || "");
		setPageCountMin(values?.page_count_min || "");
		setPageCountMax(values?.page_count_max || "");
		setLanguage(values?.language || "");
		setOpenFilters(values?.open_filters || "");
		setFocusSearchInput(values?.focus_search_input || "");
		push(`/books?${new URLSearchParams(values).toString()}`);
	};

	const changeOpenFilters = (value) => {
		if (value?.length <= 0) {
			const q = deleteParam("open_filters");
			setOpenFilters("");
			return push(`${pathname}?${q}`);
		}
		const q = setParam("open_filters", value);
		push(`${pathname}?${q}`);
	};

	const changeFocusSearchInput = (value) => {
		if (value?.length <= 0) {
			const q = deleteParam("focus_search_input");
			setFocusSearchInput("");
			return push(`${pathname}?${q}`);
		}
		const q = setParam("focus_search_input", value);
		push(`${pathname}?${q}`);
	};

	const changePage = (value) => {
		if (value?.length <= 0) {
			const q = deleteParam("page");
			setPage(1);
			return push(`${pathname}?${q}`);
		}
		const q = setParam("page", value);
		push(`${pathname}?${q}`);
	};

	const changeRowsPerPage = (value) => {
		if (value?.length <= 0) {
			const q = deleteParam("rows");
			setRowsPerPage(10);
			return push(`${pathname}?${q}`);
		}
		const q = setParam("rows", value);
		push(`${pathname}?${q}`);
	};

	const changeFirstPage = (value) => {
		if (value?.length <= 0) {
			const q = deleteParam("first_page");
			setFirstPage(0);
			return push(`${pathname}?${q}`);
		}
		const q = setParam("first_page", value);
		push(`${pathname}?${q}`);
	};

	const changeCategoryIs = (value) => {
		if (value?.length <= 0) {
			const q = deleteParam("category_is");
			setCategoryIs("");
			return push(`${pathname}?${q}`);
		}
		const q = setParam("category_is", value);
		push(`${pathname}?${q}`);
	};

	const changeGenreIs = (value) => {
		if (value?.length <= 0) {
			const q = deleteParam("genre_is");
			setGenreIs("");
			return push(`${pathname}?${q}`);
		}
		const q = setParam("genre_is", value);
		push(`${pathname}?${q}`);
	};

	const changePublisherIs = (value) => {
		if (value?.length <= 0) {
			const q = deleteParam("publisher_is");
			setPublisherIs("");
			return push(`${pathname}?${q}`);
		}
		const q = setParam("publisher_is", value);
		push(`${pathname}?${q}`);
	};

	const changeCountryIs = (value) => {
		if (value?.length <= 0) {
			const q = deleteParam("country_is");
			setCountryIs("");
			return push(`${pathname}?${q}`);
		}
		const q = setParam("country_is", value);
		push(`${pathname}?${q}`);
	};

	const changePricingModelIs = (value) => {
		if (value?.length <= 0) {
			const q = deleteParam("pricing_model_is");
			setPricingModelIs("");
			return push(`${pathname}?${q}`);
		}
		const q = setParam("pricing_model_is", value);
		push(`${pathname}?${q}`);
	};

	const changeSearchTerm = (value) => {
		if (value?.length <= 0) {
			const q = deleteParam("search_term");
			setSearchTerm("");
			return push(`${pathname}?${q}`);
		}
		const q = setParam("search_term", value);
		push(`${pathname}?${q}`);
	};

	const changePublicationDateFrom = (value) => {
		setPublicationDateFrom(value || "");
		if (!value) {
			const q = deleteParam("publication_date_from");
			return push(`${pathname}?${q}`);
		}
		push(`${pathname}?${setParam("publication_date_from", value)}`);
	};
	const changePublicationDateTo = (value) => {
		setPublicationDateTo(value || "");
		if (!value) {
			const q = deleteParam("publication_date_to");
			return push(`${pathname}?${q}`);
		}
		push(`${pathname}?${setParam("publication_date_to", value)}`);
	};
	const changePriceMin = (value) => {
		setPriceMin(value || "");
		if (!value) {
			const q = deleteParam("price_min");
			return push(`${pathname}?${q}`);
		}
		push(`${pathname}?${setParam("price_min", value)}`);
	};
	const changePriceMax = (value) => {
		setPriceMax(value || "");
		if (!value) {
			const q = deleteParam("price_max");
			return push(`${pathname}?${q}`);
		}
		push(`${pathname}?${setParam("price_max", value)}`);
	};
	const changeFormat = (value) => {
		setFormat(value || "");
		if (!value) {
			const q = deleteParam("format");
			return push(`${pathname}?${q}`);
		}
		push(`${pathname}?${setParam("format", value)}`);
	};
	const changeAvailability = (value) => {
		setAvailability(value || "");
		if (!value) {
			const q = deleteParam("availability");
			return push(`${pathname}?${q}`);
		}
		push(`${pathname}?${setParam("availability", value)}`);
	};
	const changePageCountMin = (value) => {
		setPageCountMin(value || "");
		if (!value) {
			const q = deleteParam("page_count_min");
			return push(`${pathname}?${q}`);
		}
		push(`${pathname}?${setParam("page_count_min", value)}`);
	};
	const changePageCountMax = (value) => {
		setPageCountMax(value || "");
		if (!value) {
			const q = deleteParam("page_count_max");
			return push(`${pathname}?${q}`);
		}
		push(`${pathname}?${setParam("page_count_max", value)}`);
	};
	const changeLanguage = (value) => {
		setLanguage(value || "");
		if (!value) {
			const q = deleteParam("language");
			return push(`${pathname}?${q}`);
		}
		push(`${pathname}?${setParam("language", value)}`);
	};

	return {
		openFilters,
		focusSearchInput,
		firstPage,
		rowsPerPage,
		page,
		categoryIs,
		genreIs,
		publisherIs,
		pricingModelIs,
		countryIs,
		searchTerm,
		publicationDateFrom,
		publicationDateTo,
		priceMin,
		priceMax,
		format,
		availability,
		pageCountMin,
		pageCountMax,
		language,
		changePublicationDateFrom,
		changePublicationDateTo,
		changePriceMin,
		changePriceMax,
		changeFormat,
		changeAvailability,
		changePageCountMin,
		changePageCountMax,
		changeLanguage,
		changeOpenFilters,
		changeFocusSearchInput,
		changePage,
		changeRowsPerPage,
		changeFirstPage,
		changeCategoryIs,
		changeGenreIs,
		changePublisherIs,
		changePricingModelIs,
		changeSearchTerm,
		changeCountryIs,
		changeAllFiltersAtOnce,
	};
};

export default useBooksFilters;
